import { Injectable } from "@angular/core";
// momentan entfernt, da log ein cirkelbezug hat; import { CoreLogService } from "./log.service";
import { CoreCOMService } from "./com.service";
import { EnvDir } from "./com/comEnvironmentFileSystem";
import { Mutex } from "../forked/ts-mutex";

/** UAPP Secured Environment FileSystem by Mutex Interface
 * [reference/cordova-plugin-file](https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/)
*/
export interface ISafeFileSystem {
  // Prüfung des EnvDir
  isReadOnly(env_dir: EnvDir): boolean;
  // Directories & Files
  getAllEntries(env_dir: EnvDir, dir: string): Promise<Entry[]>;
  // Directories
  getDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry>;
  getAllSubDirectories(env_dir: EnvDir, dir: string): Promise<DirectoryEntry[]>;
  createDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry>;
  removeDirectory(env_dir: EnvDir, dir: string): Promise<boolean>;
  removeAllSubDirectories(env_dir: EnvDir, dir: string): Promise<boolean>;
  // Files
  getFile(env_dir: EnvDir, dir: string, file: string): Promise<FileEntry>;
  getAllFiles(env_dir: EnvDir, dir: string): Promise<FileEntry[]>;
  createFile(env_dir: EnvDir, dir: string, file: string): Promise<FileEntry>;
  removeFile(env_dir: EnvDir, dir: string, file: string): Promise<boolean>;
  removeAllFiles(env_dir: EnvDir, dir: string): Promise<boolean>;
  writeToFile(env_dir: EnvDir, dir: string, file: string, data: Blob, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void>;
  readFromFile(env_dir: EnvDir, dir: string, file: string, progressEv?: (event: ProgressEvent) => void): Promise<ArrayBuffer>;
  writeStringToFile(env_dir: EnvDir, dir: string, file: string, data: string, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void>;
  readStringFromFile(env_dir: EnvDir, dir: string, file: string, progressEv?: (event: ProgressEvent) => void): Promise<string>;
  // Overloading for using FileSystemEntries
  getAllEntriesByEntry(directoryEntry: DirectoryEntry): Promise<Entry[]>;
  getAllFilesByEntry(directoryEntry: DirectoryEntry): Promise<FileEntry[]>;
  getAllSubDirectoriesByEntry(directoryEntry: DirectoryEntry): Promise<DirectoryEntry[]>;
  removeFileByEntry(fileEntry: FileEntry): Promise<boolean>;
  removeAllFilesByEntry(directoryEntry: DirectoryEntry): Promise<boolean>;
  removeDirectoryByEntry(directoryEntry: DirectoryEntry): Promise<boolean>;
  removeAllSubDirectoriesByEntry(directoryEntry: DirectoryEntry): Promise<boolean>;
  writeToFileByEntry(fileEntry: FileEntry, data: Blob, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void>;
  readFromFileByEntry(fileEntry: FileEntry, progressEv?: (event: ProgressEvent) => void): Promise<ArrayBuffer>;
  writeStringToFileByEntry(fileEntry: FileEntry, data: string, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void>;
  readStringFromFileByEntry(fileEntry: FileEntry, progressEv?: (event: ProgressEvent) => void): Promise<string>;
}

@Injectable({
  providedIn: "root"
})
export class SafeFileSystem implements ISafeFileSystem {

  private static _StaticMutex = new Mutex();
  // todo create mutex factory to create mutex for different usage
  // private static _CreatorMutex = new Mutex();
  // private static _CashedMutex = {};
  // private static getMutex(key: string): Mutex {}

  /** UAPP Environment FileSystem
   * [reference/cordova-plugin-file](https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file)
   * @param _coreCOMService CoreCOMService
   * @param _coreLogService CoreLogService
   */
  constructor(private _coreCOMService: CoreCOMService) {
  }

  public isReadOnly(env_dir: EnvDir): boolean {
    return this._coreCOMService.environment.fileSystem.isReadOnly(env_dir);
  }

  public getAllEntries(env_dir: EnvDir, dir: string): Promise<Entry[]> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<Entry[]>(async ()  => {
        return await me._coreCOMService.environment.fileSystem.getAllEntries(env_dir, dir);
      });
  }

  public getDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<DirectoryEntry>(async () => {
        return await me._coreCOMService.environment.fileSystem.getDirectory(env_dir, dir);
      });
  }

  public getAllSubDirectories(env_dir: EnvDir, dir: string): Promise<DirectoryEntry[]> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<DirectoryEntry[]>(async () => {
        return await me._coreCOMService.environment.fileSystem.getAllSubDirectories(env_dir, dir);
      });
  }

  public createDirectory(env_dir: EnvDir, dir: string): Promise<DirectoryEntry> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<DirectoryEntry>(async () => {
        return await me._coreCOMService.environment.fileSystem.createDirectory(env_dir, dir);
      });
  }

  public removeDirectory(env_dir: EnvDir, dir: string): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeDirectory(env_dir, dir);
      });
  }

  public removeAllSubDirectories(env_dir: EnvDir, dir: string): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeAllSubDirectories(env_dir, dir);
      });
  }

  public getFile(env_dir: EnvDir, dir: string, file: string): Promise<FileEntry> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<FileEntry>(async () => {
        return await me._coreCOMService.environment.fileSystem.getFile(env_dir, dir, file);
      });
  }

  public getAllFiles(env_dir: EnvDir, dir: string): Promise<FileEntry[]> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<FileEntry[]>(async () => {
        return await me._coreCOMService.environment.fileSystem.getAllFiles(env_dir, dir);
      });
  }

  public createFile(env_dir: EnvDir, dir: string, file: string): Promise<FileEntry> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<FileEntry>(async () => {
        return await me._coreCOMService.environment.fileSystem.createFile(env_dir, dir, file);
      });
  }

  public removeFile(env_dir: EnvDir, dir: string, file: string): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeFile(env_dir, dir, file);
      });
  }

  public removeAllFiles(env_dir: EnvDir, dir: string): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeAllFiles(env_dir, dir);
      });
  }

  public writeToFile(env_dir: EnvDir, dir: string, file: string, data: Blob, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<void>(async () => {
        return await me._coreCOMService.environment.fileSystem.writeToFile(env_dir, dir, file, data, append, progressEv);
      });
  }

  public readFromFile(env_dir: EnvDir, dir: string, file: string, progressEv?: (event: ProgressEvent) => void): Promise<ArrayBuffer> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<ArrayBuffer>(async () => {
        return await me._coreCOMService.environment.fileSystem.readFromFile(env_dir, dir, file, progressEv);
      });
  }

  public writeStringToFile(env_dir: EnvDir, dir: string, file: string, data: string, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<void>(async () => {
        return await me._coreCOMService.environment.fileSystem.writeStringToFile(env_dir, dir, file, data, append, progressEv);
      });
  }

  public readStringFromFile(env_dir: EnvDir, dir: string, file: string, progressEv?: (event: ProgressEvent) => void): Promise<string> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<string>(async () => {
        return await me._coreCOMService.environment.fileSystem.readStringFromFile(env_dir, dir, file, progressEv);
      });
  }

  public getAllEntriesByEntry(directoryEntry: DirectoryEntry): Promise<Entry[]> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<Entry[]>(async () => {
        return await me._coreCOMService.environment.fileSystem.getAllEntriesByEntry(directoryEntry);
      });
  }

  public getAllFilesByEntry(directoryEntry: DirectoryEntry): Promise<FileEntry[]> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<FileEntry[]>(async () => {
        return await me._coreCOMService.environment.fileSystem.getAllFilesByEntry(directoryEntry);
      });
  }

  public getAllSubDirectoriesByEntry(directoryEntry: DirectoryEntry): Promise<DirectoryEntry[]> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<DirectoryEntry[]>(async () => {
        return await me._coreCOMService.environment.fileSystem.getAllSubDirectoriesByEntry(directoryEntry);
      });
  }

  public removeFileByEntry(fileEntry: FileEntry): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeFileByEntry(fileEntry);
      });
  }

  /** TODO momently it rais many errors
   * @param env_dir EnvDir type
   * @param dir Directorypath
   */
  public removeAllFilesByEntry(directoryEntry: DirectoryEntry): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeAllFilesByEntry(directoryEntry);
      });
  }

  public removeDirectoryByEntry(directoryEntry: DirectoryEntry): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeDirectoryByEntry(directoryEntry);
      });
  }

  public removeAllSubDirectoriesByEntry(directoryEntry: DirectoryEntry): Promise<boolean> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<boolean>(async () => {
        return await me._coreCOMService.environment.fileSystem.removeAllSubDirectoriesByEntry(directoryEntry);
      });
  }

  public writeToFileByEntry(fileEntry: FileEntry, data: Blob, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<void>(async () => {
        return await me._coreCOMService.environment.fileSystem.writeToFileByEntry(fileEntry, data, append, progressEv);
      });
  }

  public readFromFileByEntry(fileEntry: FileEntry, progressEv?: (event: ProgressEvent) => void): Promise<ArrayBuffer> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<ArrayBuffer>(async () => {
        return await me._coreCOMService.environment.fileSystem.readFromFileByEntry(fileEntry, progressEv);
      });
  }

  public writeStringToFileByEntry(fileEntry: FileEntry, data: string, append: boolean, progressEv?: (event: ProgressEvent) => void): Promise<void> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<void>(async () => {
        return await me._coreCOMService.environment.fileSystem.writeStringToFileByEntry(fileEntry, data, append, progressEv);
      });
  }

  public readStringFromFileByEntry(fileEntry: FileEntry, progressEv?: (event: ProgressEvent) => void): Promise<string> {
    const me = this;
    return SafeFileSystem._StaticMutex.use<string>(async () => {
        return await me._coreCOMService.environment.fileSystem.readStringFromFileByEntry(fileEntry, progressEv);
      });
  }
}
